import {copyText} from '@github-ui/copy-to-clipboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CopyIcon, TerminalIcon, VscodeIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Tooltip, Truncate} from '@primer/react'
import {useCallback, useMemo} from 'react'

import styles from './OpenInLocalAgentButton.module.css'

type OpenInLocalAgentButtonProps = {
  url: string
}

function buildPrompt(issueUrl: string): string {
  return `Create a plan for the following GitHub issue: ${issueUrl}. Store the plan in .github/plans/issue-id.md`
}

function buildVSCodeAgentUrl(prompt: string): string {
  const doubleEncoded = encodeURIComponent(encodeURIComponent(prompt))
  return `vscode://github.copilot-chat?mode=agent&prompt=${doubleEncoded}`
}

export function OpenInLocalAgentButton({url}: OpenInLocalAgentButtonProps) {
  const enabled = isFeatureEnabled('issues_open_in_tool_links')
  if (!enabled) return null

  return (
    <div className={styles.container}>
      <OpenInLocalAgentButtonInner url={url} />
    </div>
  )
}

function OpenInLocalAgentButtonInner({url}: OpenInLocalAgentButtonProps) {
  const prompt = useMemo(() => buildPrompt(url), [url])
  const vsCodeUrl = useMemo(() => buildVSCodeAgentUrl(prompt), [prompt])

  const handleCopyAsPrompt = useCallback(() => {
    void copyText(prompt)
  }, [prompt])

  const handleOpenInVSCode = useCallback(() => {
    window.open(vsCodeUrl, '_self')
  }, [vsCodeUrl])

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Tooltip text="Open in coding tool" type="label">
          <ActionMenu.Button leadingVisual={TerminalIcon} size="medium" />
        </Tooltip>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay className={styles.overlay}>
        <ActionList>
          <ActionList.Item onSelect={handleCopyAsPrompt}>
            <ActionList.LeadingVisual>
              <TerminalIcon />
            </ActionList.LeadingVisual>
            Copy as prompt
            <ActionList.Description variant="block">
              <Truncate maxWidth={240} title={prompt}>
                {prompt}
              </Truncate>
            </ActionList.Description>
            <ActionList.TrailingVisual>
              <CopyIcon />
            </ActionList.TrailingVisual>
          </ActionList.Item>
          <ActionList.Divider className={styles.divider} />
          <ActionList.Group>
            <ActionList.GroupHeading>Open in</ActionList.GroupHeading>
            <ActionList.Item onSelect={handleOpenInVSCode}>
              <ActionList.LeadingVisual>
                <VscodeIcon />
              </ActionList.LeadingVisual>
              VS Code
            </ActionList.Item>
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
